/**
 * Décision 374 — la gestion d'un contrat après sa création : sa durée, sa
 * suppression quand il a été créé par erreur, sa clôture. Les mêmes règles,
 * avec les mêmes phrases, pour l'écran (bouton désactivé et sa raison) et
 * pour l'API (refus 409).
 */
/** Une durée souscrite, en mois : de 1 mois à 50 ans, comme à la souscription. */
export const DUREE_CONTRAT_MOIS_MAX = 600;
/** Le motif d'un geste sur un contrat : relu au journal, il dit quelque chose. */
export const LONGUEUR_MOTIF_MIN = 5;
/**
 * Un contrat clos ne change plus : ni son offre, ni sa durée. `null` s'il
 * est encore en cours.
 */
export function refusModificationContrat(actif, objet) {
    if (actif)
        return null;
    return objet === 'offre'
        ? 'Ce contrat est clos : son offre ne change plus.'
        : 'Ce contrat est clos : sa durée ne change plus.';
}
function jourCivil(annee, mois, jour) {
    return new Date(Date.UTC(annee, mois, jour)).toISOString().slice(0, 10);
}
function partiesDe(date) {
    const [a = 0, m = 1, j = 1] = date.split('-').map(Number);
    return [a, m - 1, j];
}
/**
 * Le dernier jour couvert : « 1 an à compter du 11/08/2026 » finit le
 * 10/08/2027. La création et la correction de la durée calculent ainsi.
 */
export function dateFinDuContrat(dateDebut, dureeMois) {
    const [a, m, j] = partiesDe(dateDebut);
    return jourCivil(a, m + dureeMois, j - 1);
}
/**
 * La durée en mois qu'une date de fin exprime exactement, ou `null` quand
 * elle tombe entre deux (un contrat du 01/01 au 15/06) : la date de fin fait
 * alors foi, et la durée n'est plus un nombre de mois.
 */
export function dureeDeLaDateFin(dateDebut, dateFin) {
    const [a1, m1] = partiesDe(dateDebut);
    const [a2, m2] = partiesDe(dateFin);
    const approchee = (a2 - a1) * 12 + (m2 - m1);
    for (const n of [approchee - 1, approchee, approchee + 1]) {
        if (n >= 1 && n <= DUREE_CONTRAT_MOIS_MAX && dateFinDuContrat(dateDebut, n) === dateFin) {
            return n;
        }
    }
    return null;
}
/**
 * Pourquoi un contrat ne se supprime pas, ou `null`. Seul un contrat créé
 * par erreur se supprime : aucune fiche ne l'a jamais porté (même supprimée :
 * ses visites et son journal le citent), aucun DNS n'y a été déposé. Sinon,
 * on le clôt — même vidé par un déplacement (décision 381). Le seul contrat
 * d'une entreprise, ou son dernier contrat en cours, reste : il faut d'abord
 * ajouter le bon.
 */
export function refusSuppressionContrat(c) {
    if (c.fichesRattachees > 0) {
        return `${String(c.fichesRattachees)} fiche${c.fichesRattachees > 1 ? 's ont' : ' a'} porté ce contrat : il ne se supprime pas, il se clôt.`;
    }
    if (c.fichesParties) {
        return 'Des salariés ont porté ce contrat avant d’en partir : il ne se supprime pas, il se clôt.';
    }
    if (c.importsDns > 0) {
        return `${String(c.importsDns)} import${c.importsDns > 1 ? 's' : ''} DNS ${c.importsDns > 1 ? 'ont' : 'a'} été déposé${c.importsDns > 1 ? 's' : ''} sur ce contrat : il ne se supprime pas, il se clôt.`;
    }
    if (c.autresContrats === 0) {
        return 'C’est le seul contrat de l’entreprise : ajoutez d’abord le bon contrat, puis supprimez celui-ci.';
    }
    if (c.actif && c.autresContratsEnCours === 0) {
        return 'C’est le dernier contrat en cours de l’entreprise : ajoutez d’abord le bon contrat, puis supprimez celui-ci.';
    }
    return null;
}
/** Décision 381 (2) — l'avertissement de la fenêtre qui clôt le dernier contrat en cours. */
export const AVERTISSEMENT_DERNIER_CONTRAT = 'Après la clôture, l’entreprise n’aura plus aucun contrat en cours : ses salariés ne seront plus couverts.';
/**
 * Décision 381 (4) — une date de fin avant le début, refusée à l'écran dès la
 * saisie et par l'API avec la même phrase. `null` si elle convient.
 */
export function refusDateFinContrat(dateDebut, dateFin) {
    if (dateFin >= dateDebut)
        return null;
    const [a, m, j] = dateDebut.split('-');
    return `La date de fin ne peut pas précéder le début du contrat (${j ?? ''}/${m ?? ''}/${a ?? ''}).`;
}
/**
 * Pourquoi un contrat ne se clôt pas encore, ou `null`. Personne ne doit y
 * rester rattaché : un salarié sur un contrat clos ne saurait plus où son
 * prochain DNS l'attend. Un DNS en cours se termine avant.
 */
export function refusClotureContrat(c) {
    if (c.personnesRattachees > 0) {
        const n = c.personnesRattachees;
        return `${String(n)} salarié${n > 1 ? 's sont' : ' est'} encore rattaché${n > 1 ? 's' : ''} à ce contrat : déplacez-${n > 1 ? 'les' : 'le'} vers un autre contrat de l’entreprise avant de le clore.`;
    }
    if (c.dnsEnCours) {
        return 'Un DNS de ce contrat attend encore d’être appliqué ou rejeté : terminez-le avant de clore le contrat.';
    }
    return null;
}
/**
 * Décision 374 (d, e) — le matricule n'est unique que dans son contrat
 * (CP-10) : un salarié ne rejoint pas un contrat où son matricule est déjà
 * porté, quelle que soit la casse. `null` s'il n'y a aucun conflit.
 */
export function refusConflitsMatricule(matricules) {
    const n = matricules.length;
    if (n === 0)
        return null;
    const lus = `${matricules.slice(0, 10).join(', ')}${n > 10 ? '…' : ''}`;
    return n === 1
        ? `Le matricule ${lus} est déjà porté sur le contrat d’arrivée : deux salariés d’un même contrat n’ont pas le même matricule. Corrigez-le avant de changer de contrat.`
        : `${String(n)} matricules sont déjà portés sur le contrat d’arrivée (${lus}) : deux salariés d’un même contrat n’ont pas le même matricule. Corrigez-les avant de déplacer.`;
}
/**
 * Décision 374 (e) — pourquoi les salariés d'un contrat ne se déplacent pas,
 * ou `null`. Un contrat clos où des fiches restent se vide aussi : c'est
 * ainsi qu'on les rattrape.
 */
export function refusDeplacementSalaries(c) {
    if (c.personnesRattachees === 0)
        return 'Aucun salarié n’est rattaché à ce contrat.';
    if (c.autresContratsEnCours === 0) {
        return 'L’entreprise n’a pas d’autre contrat en cours : ajoutez d’abord celui qui les recevra.';
    }
    return null;
}
//# sourceMappingURL=contrats.js.map